/**
 * Shop URL Service
 *
 * Manages shop subdomain URLs (e.g., shop1.bazly.in)
 *
 * Features:
 * - Check subdomain availability
 * - Get shop URL by shop ID
 * - Validate subdomain format
 */

import { apiClient } from './client';

export interface ShopUrlAvailabilityRequest {
  subdomain: string;
}

export interface ShopUrlAvailabilityResponse {
  available: boolean;
  subdomain: string;
  fullUrl?: string;
  suggestedUrl?: string;
  message?: string;
}

export interface ShopUrl {
  id: string;
  shopId: string;
  shopSubdomain: string;
  shopUrl: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

/**
 * Shop URL Service
 */
class ShopUrlService {
  private readonly baseUrl = '/api/shopurls';

  /**
   * Check if a subdomain is available for registration
   *
   * @param subdomain - Desired subdomain (e.g., 'shop1' for 'shop1.bazly.in')
   * @returns Promise<ShopUrlAvailabilityResponse>
   *
   * @example
   * ```typescript
   * const result = await shopUrlService.checkAvailability('shop1');
   * if (result.available) {
   *   console.log('Subdomain is available!');
   * }
   * ```
   */
  async checkAvailability(subdomain: string): Promise<ShopUrlAvailabilityResponse> {
    console.log('🔍 [ShopUrlService] ====== CHECKING SUBDOMAIN AVAILABILITY ======');
    console.log('🔍 [ShopUrlService] Subdomain:', subdomain);
    console.log('🔍 [ShopUrlService] Endpoint:', `${this.baseUrl}/check-availability`);

    try {
      const response = await apiClient.post<ShopUrlAvailabilityResponse>(
        `${this.baseUrl}/check-availability`,
        { subdomain }
      );

      console.log('✅ [ShopUrlService] ====== AVAILABILITY CHECK SUCCESS ======');
      console.log('✅ [ShopUrlService] Full response:', response);
      console.log('✅ [ShopUrlService] Response data:', response.data);
      console.log('✅ [ShopUrlService] Available:', response.data.available);
      console.log('✅ [ShopUrlService] Message:', response.data.message);

      // Return only the data, not the full Axios response
      return response.data;
    } catch (error: any) {
      console.error('❌ [ShopUrlService] ====== AVAILABILITY CHECK FAILED ======');
      console.error('❌ [ShopUrlService] Error:', error);
      console.error('❌ [ShopUrlService] Error message:', error.message);
      console.error('❌ [ShopUrlService] Error response:', error.response?.data);
      console.error('❌ [ShopUrlService] Error status:', error.response?.status);
      throw error;
    }
  }

  /**
   * Get shop URL by shop ID
   *
   * @param shopId - Shop ID
   * @returns Promise<ShopUrl>
   */
  async getByShopId(shopId: string): Promise<ShopUrl> {
    console.log('🔍 [ShopUrlService] Getting shop URL by shop ID:', shopId);

    try {
      const response = await apiClient.get<ShopUrl>(
        `${this.baseUrl}/shop/${shopId}`
      );

      console.log('✅ [ShopUrlService] Shop URL retrieved:', response.data);
      return response.data;
    } catch (error) {
      console.error('❌ [ShopUrlService] Failed to get shop URL:', error);
      throw error;
    }
  }

  /**
   * Validate subdomain format
   *
   * @param subdomain - Subdomain to validate
   * @returns boolean - true if valid, false otherwise
   */
  validateSubdomainFormat(subdomain: string): boolean {
    // Subdomain must be 3-63 characters and contain only lowercase letters, numbers, and hyphens
    const pattern = /^[a-z0-9-]{3,63}$/;
    return pattern.test(subdomain);
  }

  /**
   * Get validation error message for subdomain
   *
   * @param subdomain - Subdomain to validate
   * @returns string | null - Error message or null if valid
   */
  getValidationError(subdomain: string): string | null {
    if (!subdomain || subdomain.trim() === '') {
      return 'Subdomain is required';
    }

    if (subdomain.length < 3) {
      return 'Subdomain must be at least 3 characters';
    }

    if (subdomain.length > 63) {
      return 'Subdomain must be at most 63 characters';
    }

    if (!/^[a-z0-9-]+$/.test(subdomain)) {
      return 'Subdomain can only contain lowercase letters, numbers, and hyphens';
    }

    if (subdomain.startsWith('-') || subdomain.endsWith('-')) {
      return 'Subdomain cannot start or end with a hyphen';
    }

    if (subdomain.includes('--')) {
      return 'Subdomain cannot contain consecutive hyphens';
    }

    return null;
  }
}

// Export singleton instance
export const shopUrlService = new ShopUrlService();
